import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { customerV2Api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  CheckCircle2, XCircle, Clock, AlertTriangle, Loader2, ArrowRight, RefreshCw,
  Ticket, Zap,
} from 'lucide-react';

/**
 * /checkout/payment-status
 * ─────────────────────────────────────────────────────────────────
 * Interstitial page shown AFTER an event-ticket payment gateway
 * (Paytm / Razorpay) redirects back to us. Verifies the booking's
 * actual `payment_status` with the server BEFORE forwarding the user
 * to their ticket. While the booking is still `pending`, polls every
 * 3s up to ~60s before giving up and showing the manual-retry view.
 */
export default function BookingPaymentStatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const failureReason = searchParams.get('reason');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const pollTimerRef = useRef(null);

  // ── Fetch booking status ──────────────────────────────────────────
  const fetchStatus = async () => {
    try {
      const res = await customerV2Api.getBooking(bookingId);
      setBooking(res.data);
      setError(null);
      return res.data;
    } catch (e) {
      // 401 → user not logged in; bookings are still confirmed by webhook,
      // so direct them to /account/bookings via the magic short link.
      if (e?.response?.status === 401) {
        setError('Please sign in to view your booking confirmation.');
      } else {
        setError(e?.response?.data?.detail || 'Could not load booking.');
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!bookingId) { navigate('/events'); return; }
    let alive = true;

    (async () => {
      const b = await fetchStatus();
      if (!alive || !b) return;
      const isTerminal =
        b.payment_status === 'paid' ||
        b.payment_status === 'failed' ||
        b.payment_status === 'cancelled' ||
        b.booking_status === 'cancelled';
      if (!isTerminal) {
        pollTimerRef.current = setInterval(async () => {
          setPollCount((c) => c + 1);
          const nb = await fetchStatus();
          if (!alive || !nb) return;
          if (
            nb.payment_status === 'paid' ||
            nb.payment_status === 'failed' ||
            nb.payment_status === 'cancelled'
          ) {
            clearInterval(pollTimerRef.current);
          }
        }, 3000);
      }
    })();

    return () => {
      alive = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [bookingId]);

  // Auto-stop polling after ~60s (20 polls × 3s)
  useEffect(() => {
    if (pollCount >= 20 && pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
    }
  }, [pollCount]);

  // ── Auto-redirect on success ──────────────────────────────────────
  useEffect(() => {
    if (!booking) return;
    if (booking.payment_status === 'paid' && booking.booking_status === 'confirmed') {
      // Give the user a beat to see the success state before forwarding
      const t = setTimeout(() => {
        if (booking.public_ticket_token) {
          navigate(`/tickets/${booking.public_ticket_token}`, { replace: true });
        } else {
          navigate('/account/bookings', { replace: true });
        }
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [booking, navigate]);

  // ── Render ────────────────────────────────────────────────────────
  const renderBody = () => {
    if (loading && !booking) {
      return (
        <div className="text-center" data-testid="booking-status-loading">
          <Loader2 className="w-10 h-10 text-[#D4A843] animate-spin mx-auto mb-3" />
          <p className="text-[#B9B9B9] text-sm">Verifying payment…</p>
        </div>
      );
    }

    if (error && !booking) {
      return (
        <div className="text-center max-w-sm" data-testid="booking-status-error">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h2 className="font-['EB_Garamond'] text-2xl text-[#F2F2F2] mb-1.5">Something&apos;s off</h2>
          <p className="text-[#B9B9B9] text-sm mb-5">{error}</p>
          <Link to="/account/bookings">
            <Button className="btn-gold w-full">
              View My Bookings <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      );
    }

    const ps = booking?.payment_status;
    const bs = booking?.booking_status;

    // Success
    if (ps === 'paid' && bs === 'confirmed') {
      return (
        <div className="text-center max-w-sm" data-testid="booking-status-paid">
          <div className="w-16 h-16 rounded-full border-2 border-emerald-400 flex items-center justify-center mx-auto mb-4 animate-pulse-gold">
            <CheckCircle2 size={32} className="text-emerald-400" />
          </div>
          <h2 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">Payment Successful!</h2>
          <p className="text-[#B9B9B9] text-sm mb-1">Your booking is confirmed.</p>
          <p className="text-[#6F6F6F] text-xs mb-5">Taking you to your ticket…</p>
          <Loader2 className="w-5 h-5 text-[#D4A843] animate-spin mx-auto" />
        </div>
      );
    }

    // Failure / cancelled
    if (ps === 'failed' || ps === 'cancelled' || bs === 'cancelled' || failureReason) {
      const reasonText = (() => {
        switch (failureReason) {
          case 'invalid_checksum':  return 'Could not verify the gateway response.';
          case 'missing_order':     return 'Missing transaction reference.';
          default:                  return 'Your payment could not be completed.';
        }
      })();
      return (
        <div className="text-center max-w-sm" data-testid="booking-status-failed">
          <div className="w-16 h-16 rounded-full border-2 border-red-400 flex items-center justify-center mx-auto mb-4">
            <XCircle size={32} className="text-red-400" />
          </div>
          <h2 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">Payment Failed</h2>
          <p className="text-[#B9B9B9] text-sm mb-5">
            {ps === 'cancelled' ? 'You cancelled the payment.' : reasonText}{' '}
            No money was deducted. You can try again below.
          </p>
          <div className="flex flex-col gap-2">
            <Link to={booking?.event_slug ? `/events/${booking.event_slug}/checkout` : '/events'}>
              <Button className="btn-gold w-full">
                <RefreshCw size={14} className="mr-1.5" /> Try Again
              </Button>
            </Link>
            <Link to="/account/bookings">
              <Button variant="outline" className="bg-transparent border-[#3A2F16] text-[#B9B9B9] w-full">
                View My Bookings
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    // Still pending — show polling state
    return (
      <div className="text-center max-w-sm" data-testid="booking-status-pending">
        <div className="w-16 h-16 rounded-full border-2 border-[#D4A843] flex items-center justify-center mx-auto mb-4 animate-pulse-gold">
          <Clock size={32} className="text-[#D4A843]" />
        </div>
        <h2 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">Verifying Payment</h2>
        <p className="text-[#B9B9B9] text-sm mb-1">
          The bank is confirming your transaction. This usually takes under a minute.
        </p>
        <p className="text-[#6F6F6F] text-xs mb-5">
          Auto-checking… {pollCount > 0 && `(${pollCount}/20)`}
        </p>

        <div className="surface-card card-highlight rounded-xl p-4 text-left mb-4">
          <div className="text-[#6F6F6F] text-[10px] uppercase tracking-wider mb-2">Transaction Details</div>
          <div className="flex justify-between text-sm py-1">
            <span className="text-[#B9B9B9]">Booking</span>
            <span className="text-mono text-[#D4A843]">{booking?.booking_code || bookingId}</span>
          </div>
          {booking?.event_title && (
            <div className="flex justify-between text-sm py-1">
              <span className="text-[#B9B9B9]">Event</span>
              <span className="text-[#F2F2F2] truncate ml-2 max-w-[60%]">{booking.event_title}</span>
            </div>
          )}
          {booking?.total_amount != null && (
            <div className="flex justify-between text-sm py-1">
              <span className="text-[#B9B9B9]">Amount</span>
              <span className="text-[#D4A843]">₹{Number(booking.total_amount).toLocaleString('en-IN')}</span>
            </div>
          )}
          <div className="flex justify-between text-sm py-1">
            <span className="text-[#B9B9B9]">Status</span>
            <span className="text-amber-300 uppercase text-xs">{ps || 'pending'}</span>
          </div>
        </div>

        {pollCount >= 20 && (
          <div className="text-xs text-[#B9B9B9] space-y-2">
            <p>Still verifying. If you were charged, the booking will appear in your account within a few minutes.</p>
            <Link to="/account/bookings">
              <Button variant="outline" className="bg-transparent border-[#3A2F16] text-[#B9B9B9] w-full text-xs">
                <Ticket size={12} className="mr-1.5" /> Check My Bookings
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10"
         style={{ backgroundColor: '#0A0A0A' }}
         data-testid="booking-payment-status-page">
      <div className="w-full max-w-md flex flex-col items-center">
        {/* Brand */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 rounded bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
            <Zap size={13} className="text-[#120F08]" />
          </div>
          <span className="font-['EB_Garamond'] text-[#F2F2F2] text-lg font-semibold">Scratch Unleashed</span>
        </div>
        {renderBody()}
      </div>
    </div>
  );
}
